import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useSpinner } from '@/hooks/useSpinner';
import { loginService } from '@/services/login.service';
import { franchiseService } from '@/services/franchise.service';
import { telegramLogService } from '@/services/telegram-log.service';
import { getSubdomainFranchise } from '@/utils/get-subdomain';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { navigateToRoute } from '@/utils/environment-urls';
import { useAuth } from '@/hooks/useAuth';

interface Franchise {
  subdomain: string;
  name: string;
  status: string;
}

const SideLogin = () => {
  const { isLoading, showSpinner, hideSpinner } = useSpinner();
  const [franquiasDisponiveis, setFranquiasDisponiveis] = useState<Franchise[]>([]);
  const [franquiaSelecionada, setFranquiaSelecionada] = useState<string | null>(null);
  const [franquiaSelecionadaObj, setFranquiaSelecionadaObj] = useState<Franchise | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();
  const isProduction = import.meta.env.PROD;

  const { login, isAuthenticated, user } = useAuth();
  const [shouldNavigateAfterLogin, setShouldNavigateAfterLogin] = useState(false);

  const [errorLoadFranchises, setErrorLoadFranchises] = useState(false);
  const [showSignUpSheet, setShowSignUpSheet] = useState(false);

  useEffect(() => {
    if (shouldNavigateAfterLogin && isAuthenticated && user) {
      setShouldNavigateAfterLogin(false);
      
      if (user.role === 'admin') {
        navigateToRoute('/painel/home');
      } else if (user.role === 'revendedora' || user.role === 'coordenadora') {
        navigate('/home');
      } else if(user.role === 'cliente') {
        navigate('/home-cliente');
      } else {
        navigate('/nova-venda');
      }
      hideSpinner();
      return;
    }
  }, [isAuthenticated, user, shouldNavigateAfterLogin]);


  useEffect(() => {
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    // Limpar localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userlogged');

    let franchiseSubdomain = getSubdomainFranchise();

    setFranquiaSelecionada(franchiseSubdomain);
    showSpinner();

    try {
      const franchises = await franchiseService.findAllAvailable();
      setFranquiasDisponiveis(franchises);
      
      // Encontrar o objeto completo da franquia selecionada
      const selectedFranchise = franchises.find((f: Franchise) => f.subdomain === franchiseSubdomain);
      setFranquiaSelecionadaObj(selectedFranchise || null);
    } catch (err) {      
      toast.error('Erro ao carregar dados. Tente novamente ou contate o suporte.');
      setErrorLoadFranchises(true);
    } finally {
      hideSpinner();
    }
  };

  const onFranquiaChange = (franquia: string) => {
    if(!franquia) return;

    // Atualizar o estado da franquia selecionada
    setFranquiaSelecionada(franquia);
    
    // Atualizar o objeto da franquia selecionada
    const selectedFranchise = franquiasDisponiveis.find(f => f.subdomain === franquia);
    setFranquiaSelecionadaObj(selectedFranchise || null);
    
    // Só redirecionar se a franquia selecionada for diferente da atual
    if (franquia !== franquiaSelecionada) {
      showSpinner();
      
      const urlFranquia = isProduction 
        ? `https://${franquia}.xdoinfinitto.com.br/v2/authentication/side-login`
        : `http://${franquia ? franquia + "." : ""}localhost:8080/v2/authentication/side-login`;
      const urlMatriz = isProduction 
        ? `https://xdoinfinitto.com.br/v2/authentication/side-login`
        : `http://localhost:8080/v2/authentication/side-login`;
      
      if (franquia === 'matriz') {
        window.location.href = urlMatriz;
      } else {
        window.location.href = urlFranquia;
      }
    }
  };

  const openSnackBar = (message: string) => {
    setSnackbarMessage(message);
    setTimeout(() => setSnackbarMessage(''), 5000);
  };

  const check = async () => {
    // Verificar se a franquia está suspensa antes de prosseguir
    if (franquiaSelecionadaObj && franquiaSelecionadaObj.status === 'suspenso') {
      openSnackBar('Acesso temporariamente indisponível para esta unidade');
      return;
    }

    localStorage.clear();

    if (email === '' || password === '') {
      openSnackBar('Preencha todos os campos');
      return;
    }

    const logData = { errorMessage: '', loginData: email };
    showSpinner();

    try {
      const output = await loginService.checkusernameandpassword(email.trim().toLowerCase(), password, franquiaSelecionada);
      
      if (output.userData && output.idToken && output.refreshToken) {
        const nomeFranquia = franquiasDisponiveis.find((franquia) => franquia.subdomain === franquiaSelecionada)?.name || null;
        if (nomeFranquia) {
          localStorage.setItem('franquia', nomeFranquia);
        }        

        const userData = output.userData;        
        if (userData && userData.email && userData.role !== 'pendente') {
          login(userData, output.idToken, output.refreshToken);
          setShouldNavigateAfterLogin(true);
          setTimeout(() => {
            hideSpinner();
            openSnackBar('Não foi possível carregar a página, tente novamente mais tarde ou contate o suporte.');
            return;
          }, 10000);
        } else if (userData && userData.email && userData.role === 'pendente') {
          hideSpinner();
          await Swal.fire({
            icon: 'info',
            title: 'Em análise!',
            text: 'Recebemos o seu cadastro, aguarde a aprovação e o e-mail de boas-vindas.',
            confirmButtonText: 'Ok'
          });
        } else {
          hideSpinner();
          logData.errorMessage = 'Erro ao se conectar com a plataforma, tente novamente mais tarde.';
          openSnackBar(logData.errorMessage);
          logData.errorMessage += '\n > Detalhes Técnicos: <pre>' + JSON.stringify(userData) + '</pre>\n';
        }
      } else if (output.error && output.error.message !== 'Unauthorized') {
        hideSpinner();
        logData.errorMessage = 'Erro ao se conectar com a plataforma, tente novamente mais tarde!';
        openSnackBar(logData.errorMessage);
        logData.errorMessage += '\n > Detalhes Técnicos: <pre>' + JSON.stringify(output.error) + '</pre>\n';
      } else {
        hideSpinner();
        logData.errorMessage = 'Senha ou nome de usuário inválido';
        openSnackBar(logData.errorMessage);
      }

      if (logData.errorMessage) {
        telegramLogService.send(true, `+++   🔐 ERRO LOGIN:   +++\nEmail: ${logData.loginData}\n${logData.errorMessage}`, new Date());
      }
    } catch (error) {
      hideSpinner();
      logData.errorMessage = 'Erro de conexão. Tente novamente.';
      openSnackBar(logData.errorMessage);
      console.error('Erro no login:', error);
    } finally {
      //hideSpinner();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      check();
    }
  };

  const handleOpenSignUpSheet = () => {
    setShowSignUpSheet(true);
  };

  const handleCloseSignUpSheet = () => {
    setShowSignUpSheet(false);
  };

  const handleNavigateRevendedora = () => {
    setShowSignUpSheet(false);
    window.location.href = '/v2/authentication/side-register';
  };

  const handleNavigateCliente = () => {
    setShowSignUpSheet(false);
    window.location.href = '/v2/cadastro-cliente';
  };

  return (
    <div className="min-h-screen flex">
      <LoadingSpinner isLoading={isLoading} title="Aguarde" message="Carregando..." />
      
      {/* Lado esquerdo - Imagem */}
      <div className="hidden lg:flex lg:w-7/12 xl:w-8/12 bg-[#ece1cf] from-primary to-primary-hover p-6">
        <div className="flex flex-col justify-center items-center w-full">
          <img 
            src="/v2/logos/logo_x.png" 
            alt="Logo X do Infinito" 
            className="w-80 mb-8"
          />
          <img 
            src="/v2/logos/login-bg.svg" 
            alt="Login" 
            className="max-w-md"
          />
        </div>
      </div>

      {/* Lado direito - Formulário */}
      <div className="flex-1 lg:w-5/12 xl:w-4/12 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-6">
          {/* Header */}
          {/* logo for mobile only */}
          <div className="lg:hidden flex items-center justify-center mb-6">
            <img
              src="/v2/logos/logo_x.png"
              alt="Logo X do Infinito"
              className="w-60"
            />
          </div>
          <div className="text-center lg:text-left">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Bem vinda(o) ao X do Infinito
            </h1>
            <p className="text-sm text-gray-600">Plataforma de Vendas</p>
          </div>

          {/* Snackbar */}
          {snackbarMessage && (
            <Alert className="border-red-200 bg-red-50">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {snackbarMessage}
              </AlertDescription>
            </Alert>
          )}

          {/* Formulário */}
          <form className="space-y-6" onKeyPress={handleKeyPress}>
            {/* Seleção de franquia */}
            <div className="space-y-2">
              <Label htmlFor="franquia" className="text-sm font-semibold">
                Selecione sua franquia
              </Label>
              <Select 
                value={franquiaSelecionada} 
                onValueChange={onFranquiaChange}
                disabled={errorLoadFranchises || franquiasDisponiveis.length === 0}
                required
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione sua franquia" />
                </SelectTrigger>
                <SelectContent>
                  {franquiasDisponiveis.map((franquia) => (
                    <SelectItem 
                      key={franquia.subdomain} 
                      value={franquia.subdomain}
                      className="font-medium"
                    >
                      {franquia.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mensagem de franquia suspensa */}
            {franquiaSelecionadaObj && franquiaSelecionadaObj.status === 'suspenso' && (
              <Alert className="border-orange-200 bg-orange-50">
                <ExclamationTriangleIcon className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  <strong>Aviso Importante</strong>
                  <br />
                  No momento, o acesso ao aplicativo está temporariamente indisponível para sua unidade.
                  <br />
                  Em caso de dúvidas, entre em contato com nosso suporte.
                </AlertDescription>
              </Alert>
            )}

            {/* Campos de login (visíveis apenas se a franquia não estiver suspensa) */}
            {(!franquiaSelecionadaObj || franquiaSelecionadaObj.status !== 'suspenso') && (
              <>
                {/* E-mail */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold">
                    E-mail
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seuemail@exemplo.com"
                    required
                    className="w-full"
                  />
                </div>

                {/* Senha */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-semibold">
                    Senha
                  </Label>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="*******"
                    required
                    className="w-full"
                  />
                </div>

                {/* Opções */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="show-password"
                      checked={showPassword}
                      onCheckedChange={(checked) => setShowPassword(!!checked)}
                    />
                    <Label 
                      htmlFor="show-password" 
                      className="text-sm cursor-pointer"
                    >
                      Exibir senha
                    </Label>
                  </div>
                  <a 
                    href="/authentication/reset-password" 
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    Esqueceu a senha?
                    <br />
                    Recupere aqui.
                  </a>
                </div>

                {/* Botão de login */}
                <Button 
                  type="button"
                  onClick={check}
                  className="w-full bg-primary hover:bg-primary-hover"
                  disabled={isLoading || !franquiaSelecionada || errorLoadFranchises || franquiasDisponiveis.length === 0}
                >
                  Entrar
                </Button>
              </>
            )}
          </form>

          {/* Links adicionais */}
          <div className="space-y-4 text-center">
            <p className="text-sm">
              Gostaria de vender semijóias X do Infinito?{' '}
              <a 
                href="https://wa.me/45991559222" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Fale conosco!
              </a>
            </p>
            
            <hr className="border-gray-200" />
            
            <Button
              type="button"
              variant="outline"
              onClick={handleOpenSignUpSheet}
              className="w-full text-primary border-primary hover:bg-primary hover:text-white"
            >
              Cadastre-se
            </Button>
          </div>
        </div>
      </div>

      {/* Sign Up Sheet */}
      {showSignUpSheet && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white">
          {/* Header com botão voltar */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <button
              onClick={handleCloseSignUpSheet}
              className="text-gray-600 hover:text-gray-900 transition-colors"
              aria-label="Voltar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">Escolha seu cadastro</h2>
            <div className="w-6" />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Card Revendedora */}
              <div
                onClick={handleNavigateRevendedora}
                className="bg-gradient-to-br from-purple-100 to-purple-50 rounded-lg p-8 cursor-pointer transition-all hover:shadow-lg hover:scale-105 border border-purple-200"
              >
                <div className="text-center">
                  <div className="mb-4">
                    <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-purple-900 mb-4">Revendedora</h3>
                  <p className="text-purple-700 leading-relaxed">
                    Cadastre-se como revendedora para começar a vender semijóias X do Infinito e gerar renda através de comissões.
                  </p>
                  <div className="mt-6 pt-6 border-t border-purple-200">
                    <span className="inline-block bg-purple-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-purple-600">
                      Começar como Revendedora
                    </span>
                  </div>
                </div>
              </div>

              {/* Card Cliente */}
              <div
                onClick={handleNavigateCliente}
                className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-lg p-8 cursor-pointer transition-all hover:shadow-lg hover:scale-105 border border-blue-200"
              >
                <div className="text-center">
                  <div className="mb-4">
                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">Cliente</h3>
                  <p className="text-blue-700 leading-relaxed">
                    Cadastre-se como cliente para acompanhar seus pedidos, aproveitar benefícios exclusivos e gerenciar sua conta.
                  </p>
                  <div className="mt-6 pt-6 border-t border-blue-200">
                    <span className="inline-block bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-600">
                      Começar como Cliente
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SideLogin;
